import { z } from 'zod';
import type { ApiTransport } from './http.js';

/**
 * The one transport function these routes need.
 *
 * Taken as a parameter rather than the whole `ApiTransport`, on `bpSession`'s
 * reasoning: an app destructures the transport at module load, so a parameter
 * matching what it actually holds is the one that does not force it to keep the
 * object around.
 */
export type PostJson = ApiTransport['postJson'];

/**
 * The wire shapes of the passkey routes (SPEC §9.3, §12).
 *
 * **Separate from `passkey.ts` because that file imports
 * `@simplewebauthn/browser` and this one must not.** The barrel can export
 * these; it cannot export anything from `passkey.ts` as a value without pulling
 * the library into every consumer's bundle and breaking "a mocked build
 * downloads no authentication library" (ADR-0027). The ceremony needs the
 * library; the shapes do not.
 *
 * **They live in the package rather than in an app's `lib/api.ts`**, unlike
 * folders and the roster. The rule that split those is that `apps/web` knows
 * about its own domain and a package that knew about a `RecycleBin` would be
 * one both apps had to agree about. Auth routes are the other case: the package
 * is where the credential code lives, both apps will eventually reach them, and
 * neither is about anybody's files. `bpSession.ts` made the same call.
 */

export const passkeyViewSchema = z.object({
  credentialId: z.string(),
  label: z.string(),
  /** `singleDevice` | `multiDevice` — USR-11's "synced to iCloud/Google" marker. */
  deviceType: z.string(),
  backedUp: z.boolean(),
  sharedDevice: z.boolean(),
  createdAt: z.string(),
  lastUsedAt: z.string().optional(),
});

export type PasskeyView = z.infer<typeof passkeyViewSchema>;

export const passkeyListSchema = z.object({ passkeys: z.array(passkeyViewSchema) });

export const enrolledPasskeySchema = z.object({
  passkey: passkeyViewSchema,
  /**
   * How many the account now holds.
   *
   * The screen reads it to decide whether to ask for a second credential, which
   * is USR-11a rung 0 — and rung 0 is what keeps every weaker rung unused.
   */
  passkeyCount: z.number(),
});

export type EnrolledPasskey = z.infer<typeof enrolledPasskeySchema>;

export const passkeyRenamedSchema = z.object({ passkey: passkeyViewSchema });

/**
 * What signing in with a passkey answers.
 *
 * The same shape the passcode answers, deliberately: the SPA reads one body
 * whichever route signed it in, rather than branching on which credential got it
 * there. `restricted` is always `false` here — a passkey session is never
 * restricted, which is the whole of what SYS-10 buys.
 *
 * `session` is the ADR-0059 handle, and it is optional because it is present in
 * exactly one arrangement: a build that can hold no cookie and must carry the
 * credential itself. Absent everywhere else, including prod, where the
 * `Set-Cookie` on this same response is the whole of it.
 */
export const passkeySignedInSchema = z.object({
  restricted: z.boolean(),
  session: z.string().optional(),
});

export const passkeyRemovedSchema = z.object({
  passkeyCount: z.number(),
  /** Sessions the removal ended, so the consequence can be shown rather than implied. */
  sessionsEnded: z.number(),
});

export interface EnrollPasskeyInput {
  label: string;
  /**
   * USR-11's device question.
   *
   * Sent to the options route, which turns it into `authenticatorAttachment`,
   * and sent **again** to the verify route, which checks it against what the
   * attestation actually reports. The duplication is the point: the server
   * cannot infer from an attestation what the person was asked, so it has to be
   * told — and it does not trust what it is told (SEC-5).
   */
  sharedDevice: boolean;
}

/**
 * Which routes a ceremony talks to (**ADR-0085**).
 *
 * **The console runs the same ceremonies against different endpoints.** An
 * operator's registration and assertion are verified by `operator/*` routes,
 * because they resolve an operator rather than a principal and §5.1 gives the
 * platform operator no agency for the product's own routes to resolve against.
 * The *ceremony* is identical — same library, same challenge, same response
 * shape — so the paths are a parameter and the code is not duplicated.
 *
 * _ADR-0085 shared `assertOptions` between the two, and **ADR-0087** stopped._
 * The reasoning was that the route is byte-identical for every caller — a
 * discoverable credential carries no address to vary on. True, and beside the
 * point: the options it issues name the **relying party** the browser binds the
 * assertion to, and OPS-1's second origin cannot share one. The console asking
 * the product's route got a challenge for the wrong RP id and the browser
 * refused the ceremony before it began.
 */
export interface PasskeyRoutes {
  registerOptions: string;
  registerVerify: string;
  assertOptions: string;
  assertVerify: string;
}

/** What `apps/web` uses, and the default everywhere the caller says nothing. */
export const PRODUCT_PASSKEY_ROUTES: PasskeyRoutes = {
  registerOptions: '/auth/passkey/register/options',
  registerVerify: '/auth/passkey/register/verify',
  assertOptions: '/auth/passkey/assert/options',
  assertVerify: '/auth/passkey/assert/verify',
};

/** What `apps/admin` uses (OPS-2 as amended, **ADR-0085**). */
export const OPERATOR_PASSKEY_ROUTES: PasskeyRoutes = {
  registerOptions: '/operator/auth/passkey/register/options',
  registerVerify: '/operator/auth/passkey/register/verify',
  assertOptions: '/operator/auth/passkey/assert/options',
  assertVerify: '/operator/auth/passkey/assert/verify',
};
